import type { FC } from "react";
import { useRef, useState } from "react";
import { FileText } from "lucide-react";

import { useUploadDocument } from "@/api/documents";
import GenericModal from "@/components/modals/generic-modal";
import { cn } from "@/lib/utils";
import type { Patient } from "@/types/api/patient";

interface CreateDocumentModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  patient: Patient;
  isPeriotools: boolean;
}

const acceptedExtensions = ["pdf", "jpg", "jpeg", "png", "webp", "svg"];

const CreateDocumentModal: FC<CreateDocumentModalProps> = ({
  open,
  setOpen,
  patient,
  isPeriotools,
}) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadDocument();

  const handleFile = (f: File) => {
    const extension = f.name.includes(".") ? f.name.split(".").pop()! : "";
    if (acceptedExtensions.includes(extension)) {
      setError(undefined);
      setFile(f);
      setTitle(f.name.replace(/\.[^/.]+$/, "")); // Set title without extension
    } else {
      setError(
        "Le fichier doit être au format .pdf, .jpg, .jpeg, .png, .webp ou .svg"
      );
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!file || !title) return;
    await uploadMutation.mutateAsync({
      title,
      file,
      patientId: patient.id,
      isPeriotools,
    });
    setFile(null);
    setTitle("");
    setOpen(false);
  };

  return (
    <GenericModal
      open={open}
      setOpen={setOpen}
      title="Ajouter un fichier"
      logo={<FileText className="w-6 h-6" color="black" />}
      submitLabel="Continuer"
      handleSubmit={handleSubmit}
      isSubmitPending={uploadMutation.isPending}
    >
      <div className="flex flex-col gap-5 px-6">
        <div
          className={cn(
            "flex justify-center items-center bg-brand-50 rounded-xl border border-brand-700 border-dashed py-8 px-6 text-sm font-semibold text-brand-700 cursor-pointer transition",
            isDragging && "bg-brand-100 border-brand-900"
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {file ? (
            <span className="text-brand-800">{file.name}</span>
          ) : (
            "Cliquez ici ou glissez le fichier à importer"
          )}
        </div>

        {error ? <p className="text-error-700">{error}</p> : null}

        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => {
            if (e.target.files?.[0]) {
              handleFile(e.target.files[0]);
            }
          }}
          accept=".pdf,.jpg,.jpeg,.png,.webp,.svg"
        />

        <input
          type="text"
          placeholder="Titre du document"
          className="border rounded p-2 text-sm"
          hidden
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
    </GenericModal>
  );
};

export default CreateDocumentModal;
